import { ValueSetExpansionContains } from "@medplum/fhirtypes";

import {
  NewbornPostpartumColorCodes,
  NewbornPostpartumHeartRateCodes,
} from "@/encounters/constants/newborn-postpartum";
import {
  QuiltedHealthCodes,
  QuiltedHealthCodeSystems,
  QuiltedHealthValueSets,
} from "@/fhir/enums/quilted-health";
import { isEmptyAnswerCoding } from "@/intake-forms/utils/multi-select";

function sortByCustomOrder(
  orderedCodes: string[],
  options: ValueSetExpansionContains[],
): ValueSetExpansionContains[] {
  const rank = new Map<string, number>(orderedCodes.map((code, index) => [code, index]));

  return [...options].sort((a, b) => {
    const rankA = rank.get(a.code ?? "") ?? Number.MAX_SAFE_INTEGER;
    const rankB = rank.get(b.code ?? "") ?? Number.MAX_SAFE_INTEGER;
    return rankA - rankB;
  });
}

/**
 * Hoists "empty answers" (eg None) to the top of a multi-answer option list.
 *
 * ValueSet expansion returns codes in alphabetical order, which buries "None" mid-list, and neither
 * multi-answer field sorts its options. A client is meant to see the "nothing applies" answer before
 * scanning the whole list, so it is pulled to the front here instead of relying on seed order, which
 * the expansion discards. The relative order of the remaining options is preserved.
 */
export function sortEmptyAnswerOptionsFirst(
  options: ValueSetExpansionContains[],
): ValueSetExpansionContains[] {
  const emptyAnswers = options.filter((option) => isEmptyAnswerCoding(option));
  if (!emptyAnswers.length) return options;

  return [...emptyAnswers, ...options.filter((option) => !isEmptyAnswerCoding(option))];
}

export function sortValueSetOptionsByUrl(
  url: string | undefined,
  options: ValueSetExpansionContains[],
): ValueSetExpansionContains[] {
  if (!url) return options;

  // Because L&D uses ValueSet expansion, codes are returned in alphabetical order.
  // These specific ValueSets require a custom order in the UI.
  if (url === QuiltedHealthValueSets.NewbornHeartRate) {
    const orderedCodes = [
      NewbornPostpartumHeartRateCodes.AbsentCode,
      NewbornPostpartumHeartRateCodes.Under100Code,
      NewbornPostpartumHeartRateCodes.Over100Code,
    ];
    return sortByCustomOrder(orderedCodes, options);
  }

  if (url === QuiltedHealthValueSets.BabyColor) {
    const orderedCodes = [
      NewbornPostpartumColorCodes.OtherCode,
      NewbornPostpartumColorCodes.PaleOrBlueCode,
      NewbornPostpartumColorCodes.PinkBodyWithBlueExtremitiesCode,
      NewbornPostpartumColorCodes.PinkCode,
    ];
    return sortByCustomOrder(orderedCodes, options);
  }

  if (
    url === QuiltedHealthCodeSystems.ShoulderDystociaManeuvers ||
    url === QuiltedHealthValueSets.ShoulderDystociaManeuvers
  ) {
    return [...options].sort((a, b) => {
      const codeA = a.code ?? "";
      const codeB = b.code ?? "";

      const isEpisiotomyA = codeA === QuiltedHealthCodes.ManeuverEpisiotomy;
      const isEpisiotomyB = codeB === QuiltedHealthCodes.ManeuverEpisiotomy;
      const isClavicleA = codeA === QuiltedHealthCodes.ManeuverClavicleFracture;
      const isClavicleB = codeB === QuiltedHealthCodes.ManeuverClavicleFracture;

      if (isClavicleA && !isClavicleB) return 1;
      if (!isClavicleA && isClavicleB) return -1;

      if (isEpisiotomyA && !isEpisiotomyB) {
        return isClavicleB ? -1 : 1;
      }
      if (!isEpisiotomyA && isEpisiotomyB) {
        return isClavicleA ? 1 : -1;
      }

      return 0;
    });
  }

  return options;
}
